import { useState, useEffect, useMemo } from "react";
import { sizeNameMap } from "../utils/sizeNameUtils";
import { applyAllFilters } from "../utils/filterUtils";

/**
 * Group fish by scientific name and size, calculating aggregated metadata
 * @param {Array} fishData - Filtered fish data
 * @param {Object} searchResults - Map of scientific names to search ranks
 * @param {Function} getHistoricalPrice - Function to get historical price data
 * @returns {Object} - Grouped fish data keyed by scientific name
 */
export function groupFishByScientificNameAndSize(
  fishData,
  searchResults,
  getHistoricalPrice,
) {
  const groupedFish = {};
  fishData.forEach((fish) => {
    const key = fish.scientific_name;
    if (!groupedFish[key]) {
      groupedFish[key] = {
        common_name: fish.common_name,
        scientific_name: fish.scientific_name,
        image_url: fish.image_url,
        sizes: {},
        sizes_meta: {},
        min_min_price: 0,
        max_max_savings: 0,
        max_max_savings_percentage: 0,
        available_sizes_str: "",
        is_freshwater: fish.is_freshwater,
        is_saltwater: fish.is_saltwater,
        search_rank: searchResults[fish.scientific_name] || 0,
        avg_rating: fish.avg_rating,
        review_count: fish.review_count,
      };
    }
    if (!groupedFish[key].sizes[fish.size]) {
      groupedFish[key].sizes[fish.size] = [];
    }
    groupedFish[key].sizes[fish.size].push(fish);
  });

  // Sort listings by price within each size group
  Object.values(groupedFish).forEach((group) => {
    Object.values(group.sizes).forEach((sizeGroup) => {
      sizeGroup.sort(
        (a, b) =>
          a.price / a.number_of_individuals - b.price / b.number_of_individuals,
      );
    });
  });

  Object.values(groupedFish).forEach((group) => {
    var min_min_price = Infinity;
    var max_max_savings = 0;
    var max_max_savings_percentage = 0;
    var max_unique_days_count = 1;
    Object.values(group.sizes).forEach((sizeGroup) => {
      var min_price = (
        sizeGroup[0].price / sizeGroup[0].number_of_individuals
      ).toFixed(2);
      var max_price = (
        sizeGroup[sizeGroup.length - 1].price /
        sizeGroup[sizeGroup.length - 1].number_of_individuals
      ).toFixed(2);
      var avg_price = (
        sizeGroup.reduce((sum, fish) => sum + fish.price, 0) /
        sizeGroup.reduce((sum, fish) => sum + fish.number_of_individuals, 0)
      ).toFixed(2);

      var comparison_price = avg_price; // fallback when historical data is unavailable
      var unique_days_count = 1;
      var has_historical_data = false;

      // Get historical price data for better savings calculation
      var historicalData = getHistoricalPrice(
        group.scientific_name,
        sizeGroup[0].size,
      );
      if (
        !historicalData ||
        (!historicalData.avg_price && !historicalData.median_price)
      ) {
        var numericSize = Number(sizeGroup[0].size);
        if (!isNaN(numericSize)) {
          // Try variations of the numeric size to find historical data
          const sizesToTry = [];
          if (numericSize % 1 === 0) {
            // Whole number - try one less and one more
            sizesToTry.push(numericSize + 1, numericSize - 1);
          } else {
            // Decimal - try rounded down and rounded up
            sizesToTry.push(Math.ceil(numericSize), Math.floor(numericSize));
          }

          for (const trySize of sizesToTry) {
            var tryHistoricalData = getHistoricalPrice(
              group.scientific_name,
              trySize,
            );
            if (
              tryHistoricalData &&
              (tryHistoricalData.avg_price || tryHistoricalData.median_price)
            ) {
              historicalData = tryHistoricalData;
              break;
            }
          }
        }
      }

      if (
        historicalData &&
        (historicalData.avg_price || historicalData.median_price)
      ) {
        // Keep the historical benchmark independent of today's offers.
        comparison_price = Math.min(
          historicalData.avg_price ?? Infinity,
          historicalData.median_price ?? Infinity,
        );
        unique_days_count = historicalData.unique_days_count || 1;
        has_historical_data = true;
      }

      var max_savings = (comparison_price - min_price).toFixed(2);
      var max_savings_percentage =
        comparison_price > 0
          ? ((max_savings / comparison_price) * 100).toFixed(2)
          : 0;
      var min_savings = (comparison_price - max_price).toFixed(2);
      var min_savings_percentage =
        comparison_price > 0
          ? ((min_savings / comparison_price) * 100).toFixed(2)
          : 0;
      var current_max_savings = (avg_price - min_price).toFixed(2);
      var current_max_savings_percentage = avg_price > 0
        ? ((current_max_savings / avg_price) * 100).toFixed(2) : 0;

      min_min_price = Math.min(min_min_price, min_price);
      max_max_savings = Math.max(max_max_savings, max_savings);
      max_max_savings_percentage = Math.max(
        max_max_savings_percentage,
        max_savings_percentage,
      );
      max_unique_days_count = Math.max(
        max_unique_days_count,
        unique_days_count,
      );
      groupedFish[group.scientific_name].sizes_meta[sizeGroup[0].size] = {
        min_price,
        max_price,
        avg_price,
        current_comparison_price: avg_price,
        current_max_savings,
        current_max_savings_percentage,
        max_savings,
        max_savings_percentage,
        min_savings,
        min_savings_percentage,
        unique_days_count,
        has_historical_data,
        historical_comparison_price: has_historical_data ? comparison_price : null,
      };
    });

    groupedFish[group.scientific_name].min_min_price = min_min_price;
    groupedFish[group.scientific_name].max_max_savings = max_max_savings;
    groupedFish[group.scientific_name].max_max_savings_percentage =
      max_max_savings_percentage;
    groupedFish[group.scientific_name].max_unique_days_count =
      max_unique_days_count;

    var min_size = Math.min(
      ...Object.keys(group.sizes)
        .map(Number)
        .filter((size) => !isNaN(size) && size > 0),
    );
    var max_size = Math.max(
      ...Object.keys(group.sizes)
        .map(Number)
        .filter((size) => !isNaN(size) && size > 0),
    );
    groupedFish[group.scientific_name].available_sizes_str =
      isFinite(min_size) && isFinite(max_size)
        ? min_size != max_size
          ? `${min_size}" - ${max_size}"`
          : `${min_size}"`
        : "Unknown Size";
  });

  return groupedFish;
}

/**
 * Custom hook to manage listings data with in-memory filtering and grouping
 * @param {Array} listingData - Raw listing data from API
 * @param {string} dataType - 'fish' or 'inverts'
 * @param {Object} filters - Filter criteria
 * @param {number} minSize - Minimum size value
 * @param {number} maxSize - Maximum size value
 * @param {Array} availableStores - List of all available stores
 * @param {Array} availableNonNumericSizes - List of all available non-numeric sizes
 * @param {boolean} storesInitialized - Whether stores have been initialized
 * @param {Array} categoryData - Category data for filtering
 * @param {Array} historicalPriceData - Historical price data for calculations
 * @param {boolean} includeGrouping - Whether to calculate grouped fish data
 * @returns {Object} - Filtered data, grouped data (if requested), and loading states
 */
export function useListingsDatabase(
  listingData,
  dataType = "fish",
  filters = {},
  minSize = 0,
  maxSize = 36,
  availableStores = [],
  availableNonNumericSizes = [],
  storesInitialized = false,
  categoryData = [],
  historicalPriceData = [],
  includeGrouping = false,
  loadingListings = false,
  loadingHistoricalPrices = false,
) {
  const [filteredListings, setFilteredListings] = useState([]);
  const [groupedFish, setGroupedFish] = useState({});
  const [isQuerying, setIsQuerying] = useState(true);

  // Create historical price lookup map
  const historicalPriceMap = useMemo(() => {
    const map = {};
    if (historicalPriceData) {
      historicalPriceData.forEach((item) => {
        const key = `${item.scientific_name.toLowerCase()}_${item.size}`;
        map[key] = item;
      });
    }
    return map;
  }, [historicalPriceData]);

  // Function to get historical price data for a specific species and size
  const getHistoricalPrice = (scientificName, size) => {
    const key = `${scientificName.toLowerCase()}_${size}`;
    return historicalPriceMap[key];
  };

  // Filter and group data when filters or data changes
  useEffect(() => {
    if (loadingListings || loadingHistoricalPrices) {
      return;
    }

    // Use setTimeout to avoid blocking the UI
    const timeoutId = setTimeout(() => {
      try {
        const searchResults = filters.searchResults || {};
        const userFavorites = filters.userFavorites || [];
        const userSubscriptions = filters.userSubscriptions || [];
        const reefWizardRecommendations =
          filters.reefWizardRecommendations || [];

        // Apply all filters
        const filtered =
          listingData == null
            ? []
            : listingData.filter((listing) =>
                applyAllFilters(
                  listing,
                  filters,
                  searchResults,
                  categoryData,
                  userFavorites,
                  userSubscriptions,
                  reefWizardRecommendations,
                ),
              );

        setFilteredListings(filtered);

        // Calculate grouped fish if requested
        if (includeGrouping) {
          const grouped = groupFishByScientificNameAndSize(
            filtered,
            searchResults,
            getHistoricalPrice,
          );
          setGroupedFish(grouped);
        }

      } catch (error) {
        console.error("Error processing data:", error);
      } finally {
        setIsQuerying(false);
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [
    listingData,
    dataType,
    filters.scientificFilter,
    filters.genusFilter,
    filters.familyFilter,
    filters.orderFilter,
    filters.classFilter,
    filters.phylumFilter,
    filters.allowedStores,
    filters.freshwaterFilter,
    filters.saltwaterFilter,
    filters.categoryFilter,
    filters.categoryData,
    filters.searchResults,
    filters.searchTerm,
    filters.favoritesOnly,
    filters.notificationsOnly,
    filters.userFavorites,
    filters.userSubscriptions,
    filters.reefWizardOnly,
    filters.reefWizardTankFilter,
    filters.reefWizardRecommendations,
    filters.sizeFilter,
    filters.includeNumericSizes,
    filters.includeNonNumericSizes,
    filters.nonNumericSizeFilter,
    filters.careGuidesMap,
    filters.careFilters,
    filters.hasCareGuide,
    categoryData,
    includeGrouping,
    historicalPriceMap,
    loadingListings,
    loadingHistoricalPrices,
  ]);

  return {
    filteredListings,
    groupedFish,
    isQuerying,
  };
}
